import {
    AgnosticRouteObject,
    createBrowserHistory,
    createRouter,
    Location,
    RouterState,
    joinPaths,
    generatePath,
    Path,
    AgnosticDataRouteMatch,
} from "@remix-run/router";

export type { Location, RouterState };
export type Route = AgnosticRouteObject;
export type RouteMatch = AgnosticDataRouteMatch;

export interface BrowserRouterOptions {
    routes: Route[];
    basename?: string;
}

export interface RouteOptions {
    params?: PathParams;
    query?: QueryParams;
    hash?: string;
}

export interface HrefOptions {
    absolute?: boolean;
}

export interface NavigateOptions {
    replace?: boolean;
}

export type BrowserRouter = ReturnType<typeof createBrowserRouter>;
export type PathParams = Record<string, string | number | boolean>;
export type QueryParams = Record<string, string> | string[][] | URLSearchParams;

export function createBrowserRouter({ routes, basename }: BrowserRouterOptions) {
    const router = createRouter({ routes, basename, history: createBrowserHistory() });

    const onClickHandler = (event: MouseEvent) => {
        if (event.button !== 0) {
            // not a left-click; let the browser do its thing...
            return;
        }

        if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
            // user is holding a modifier key when clicking to e.g. open in
            // a new tab or a new window; we let the browser do this...
            return;
        }

        const { target } = event;
        const rootAnchorElement = findRootAnchorElement(target);

        if (rootAnchorElement) {
            if (rootAnchorElement.origin !== window.origin) {
                // href of the <a> links to another page; goodbye!
                return;
            }

            // it's a click on a <a> within the current 'site'; so we prevent
            // the browser from reloading the page and handle the "click" by
            // instructing the router to navigate to the href instead
            event.preventDefault();
            router.navigate(rootAnchorElement.href);
        }

        //  The click was not on an anchor element or an element which is wrapped by an anchor element.
        //  So this function should not interfere at all.
    };

    const initialize = () => {
        document.body.addEventListener("click", onClickHandler);
        router.initialize();
    };

    const subscribe = (subscriber: (state: RouterState) => void) => {
        router.subscribe(subscriber);
    };

    const getState = () => router.state;

    const createPath = (id: string, routeOpts: RouteOptions = {}): Path => {
        const { params = {}, query = undefined, hash = "" } = routeOpts;

        const routePath = findRouteByID(router.routes, id);

        if (routePath.length === 0) {
            console.warn("no known route with id", id);
        }

        const encodedParams = Object.fromEntries(
            Object.entries(params).map(([name, value]) => [name, encodeURIComponent(value)])
        );

        // the remix-run router lib is rather lacking, so we apparently have to
        // do all of this by ourselves; are there any decent alternatives?

        const basepath = router.basename || "/";
        const pathitems = routePath.map((route) => (route.path ? generatePath(route.path, encodedParams) : ""));
        const pathname = joinPaths([basepath, ...pathitems]);
        const search = query ? new URLSearchParams(query).toString() : "";

        return { pathname, search, hash };
    };

    const createHref = (id: string, routeOpts: RouteOptions = {}, hrefOpts?: HrefOptions) => {
        const href = router.createHref(createPath(id, routeOpts) as Location);

        if (hrefOpts && hrefOpts.absolute) {
            return `${window.location.origin}${href}`;
        }

        return href;
    };

    const navigate = (id: string, routeOpts: RouteOptions = {}, navOpts?: NavigateOptions) => {
        setTimeout(() => router.navigate(createPath(id, routeOpts), navOpts), 0);
    };

    return {
        initialize,
        subscribe,
        getState,
        createPath,
        createHref,
        navigate,
    };
}

/**
 * Finds the route that matches the given `id` along with all its ancestors. If
 * no route was found, returns an empty array.
 */
function findRouteByID(routes: AgnosticRouteObject[], id: string): AgnosticRouteObject[] {
    for (const route of routes) {
        if (route.id === id) {
            return [route];
        }

        if (route.children) {
            const childPath = findRouteByID(route.children, id);

            if (childPath.length !== 0) {
                return [route, ...childPath];
            }
        }
    }

    return [];
}

function isHtmlElement(object: any): Boolean {
    return object != null && typeof object.tagName === "string";
}

function findRootAnchorElement(object: any): HTMLAnchorElement | null {
    const anchorElement = isHtmlElement(object) && object.tagName === "A";

    if (anchorElement) {
        return object;
    }
    if (object.parentElement) {
        return findRootAnchorElement(object.parentElement);
    }
    return null;
}
